/** DEMO ONLY — reusable editor for an ordered band set. */
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Box,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  DeleteOutline,
  Add,
} from "@mui/icons-material";
import { validateBandSet } from "./bandValidation";

const num = (v) => (v === "" || v === null ? "" : Number(v));

export default function BandSetEditor({
  bands,
  onChange,
  requireContiguous = false,
  showErrors = true,
}) {
  const errors = validateBandSet(bands, { requireContiguous });

  const update = (i, patch) => {
    const next = bands.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
    onChange(next);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= bands.length) return;
    const next = [...bands];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const remove = (i) => onChange(bands.filter((_, idx) => idx !== i));

  const add = () => {
    const last = bands[bands.length - 1];
    const start = last && last.maxQty !== null ? last.maxQty + 1 : 1;
    // insert before the open-ended band if there is one
    const openIdx = bands.findIndex((b) => b.maxQty === null);
    const newBand = { minQty: start, maxQty: start + 9, label: "" };
    if (openIdx === -1) {
      onChange([...bands, newBand]);
    } else {
      const next = [...bands];
      next.splice(openIdx, 0, newBand);
      onChange(next);
    }
  };

  return (
    <Box>
      <Table size="small" sx={{ mb: 1 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#D5F9FC" }}>
            <TableCell sx={{ fontWeight: 600, width: 110 }}>Min qty</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 130 }}>Max qty</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Label</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 130 }} align="right">
              Order
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bands.map((b, i) => {
            const isOpen = b.maxQty === null;
            return (
              <TableRow key={i}>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    value={b.minQty}
                    onChange={(e) =>
                      update(i, { minQty: num(e.target.value) })
                    }
                    inputProps={{ min: 1 }}
                  />
                </TableCell>
                <TableCell>
                  {isOpen ? (
                    <Tooltip title="This is the open-ended top band">
                      <span style={{ color: "rgba(0,0,0,0.5)" }}>and above</span>
                    </Tooltip>
                  ) : (
                    <TextField
                      type="number"
                      size="small"
                      variant="standard"
                      value={b.maxQty}
                      onChange={(e) =>
                        update(i, { maxQty: num(e.target.value) })
                      }
                    />
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    variant="standard"
                    fullWidth
                    placeholder="e.g. 15-29"
                    value={b.label}
                    onChange={(e) => update(i, { label: e.target.value })}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}>
                    <ArrowUpward fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => move(i, 1)}
                    disabled={i === bands.length - 1}
                  >
                    <ArrowDownward fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => remove(i)}
                    disabled={bands.length <= 1}
                  >
                    <DeleteOutline fontSize="inherit" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Button size="small" startIcon={<Add />} onClick={add}>
        Add band
      </Button>
      <Button
        size="small"
        onClick={() => {
          // toggle the last band open-ended
          const next = bands.map((b, idx) =>
            idx === bands.length - 1
              ? { ...b, maxQty: b.maxQty === null ? b.minQty + 9 : null }
              : b
          );
          onChange(next);
        }}
        sx={{ ml: 1 }}
      >
        Toggle "and above" on last band
      </Button>

      {showErrors && errors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {errors.map((e, idx) => (
              <li key={idx}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Box>
  );
}
